import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Separator } from "@/components/ui/separator/separator";
import { getOneLoanRequestQueryOptions } from "@/config/queries/query-options";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table/table";
import { DataTable } from "./_components/repayment-schedule-table/data-table";
import { columns } from "./_components/repayment-schedule-table/columns";
import { RepaymentRequests } from "./_components/repayment-requests/repayment-requests";
import { EditRepaymentSchedule } from "./_components/edit-repayment-schedule-modal";
import { Button } from "@/components/ui/button/button";
import { IoArrowBackOutline } from "react-icons/io5";

const IndividualLoanPageByAdmin = () => {
  const adminKey = Cookies.get("key") as string;
  const { loanId } = useParams() as {
    loanId: string;
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const empId = searchParams.get("employee");

  const { data, isPending, isFetching } = useQuery(
    getOneLoanRequestQueryOptions(adminKey, loanId)
  );

  const repaymentScheduleData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.loan === undefined) {
      return [];
    }

    const result = data?.loan?.repaymentSchedule?.map((item) => {
      return {
        id: item?._id,
        amount: item?.amount,
        createdAt: item?.createdAt,
        dueDate: item?.dueDate,
        paidAmount: item?.paidAmount,
        status: item?.status,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  useEffect(() => {
    if ((!empId || empId === "") && data) {
      setSearchParams(
        {
          employee: data?.loan?.employeeId?._id,
        },
        {
          replace: false,
        }
      );
    }
  }, [empId, data]);

  return (
    <div className="p-3 min-h-[calc(100%-56px)] overflow-y-auto">
      <div className="flex items-center gap-4">
        <Button size="icon" variant="ghost" asChild>
          <Link to="/admin/other-loans">
            <IoArrowBackOutline />
          </Link>
        </Button>
        <h3 className="text-2xl font-medium underline underline-offset-4">
          Loan
        </h3>
      </div>
      <div className="py-5 mt-3">
        <div className="max-w-5xl mx-auto">
          <div className="mb-3 flex gap-3">
            <span>Loan Taken By :</span>
            <span className="text-[17px] font-medium">
              {data?.loan?.employeeId?.name} ({data?.loan?.employeeId?.email})
            </span>
          </div>
          <Table className="border">
            <TableCaption>Summary of Loan</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className="w-auto">Total Requested Amount</TableHead>
                <TableHead>Total Approved Amount</TableHead>
                <TableHead>Interest Rate</TableHead>
                <TableHead className="text-right">
                  Total Repayment Amount
                </TableHead>
                <TableHead className="text-right">Remaining Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">
                  ₹{data?.loan?.totalAmountRequested}
                </TableCell>
                <TableCell className="text-center">
                  ₹{data?.loan?.totalAmountApproved}
                </TableCell>
                <TableCell className="text-center">
                  {data?.loan?.interestRate}
                </TableCell>
                <TableCell className="text-center">
                  {data?.loan?.totalRepaymentAmount
                    ? `₹${(data?.loan?.totalRepaymentAmount as number)?.toFixed(
                        3
                      )}`
                    : `-`}
                </TableCell>

                <TableCell className="text-center">
                  {data?.loan?.remainingBalance
                    ? `₹${(data?.loan?.remainingBalance as number)?.toFixed(3)}`
                    : `-`}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <Separator className="my-4" />

        <div className="px-3 md:px-6">
          <h2 className="mb-4 text-lg font-medium">Repayment Schedules</h2>
          {/* Repayment Schedule Data Table */}
          <DataTable data={repaymentScheduleData!} columns={columns} />

          <EditRepaymentSchedule />
        </div>

        <div className="px-3 md:px-6">
          <Separator className="mt-4" />

          <RepaymentRequests />
        </div>
      </div>
    </div>
  );
};

export default IndividualLoanPageByAdmin;
